import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Eye, EyeOff, AlertCircle, CheckCircle, Search, Building2 } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    role: '',
    phone: '',
    dq_company_ids: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [existingDriver, setExistingDriver] = useState(null);
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const [tourRole, setTourRole] = useState('Lead Driver');
  const { register } = useAuth();
  const navigate = useNavigate();

  const roles = [
    { value: 'driver', label: 'Driver - Looking for gigs', description: 'Browse and apply for driving positions in the entertainment industry' },
    { value: 'bus_company', label: 'Bus Company - Hiring drivers', description: 'Post jobs and manage your fleet operations' },
    { value: 'guest_tour', label: 'Tour Organizer - Need drivers', description: 'Post tour requirements and hire qualified drivers' }
  ];

  // Load companies when component mounts or when role changes to driver
  useEffect(() => {
    if (formData.role === 'driver') {
      loadCompanies();
    }
  }, [formData.role]);

  // Check for existing driver account when tour organizer is selected
  useEffect(() => {
    if (formData.role !== 'guest_tour' || !formData.email || formData.email.length < 5) {
      setExistingDriver(null);
      setShowLinkPrompt(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${BACKEND_URL}/api/auth/check-existing-driver`, { email: formData.email });
        if (res.data.exists) {
          setExistingDriver(res.data);
          setShowLinkPrompt(true);
        } else {
          setExistingDriver(null);
          setShowLinkPrompt(false);
        }
      } catch { setExistingDriver(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.email, formData.role]);

  // Pre-select role from ?role= query param (landing-page segmentation buttons)
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('role');
    if (!param) return;
    const map = {
      driver: 'driver',
      operator: 'bus_company',
      company: 'bus_company',
      bus_company: 'bus_company',
      tour: 'guest_tour',
      guest_tour: 'guest_tour',
    };
    const mapped = map[param.toLowerCase()];
    if (mapped) {
      setFormData((prev) => ({ ...prev, role: mapped }));
    }
  }, []);


  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/companies/list`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error loading companies:', error);
      // Don't show error to user, just log it
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Filter companies based on search
  const filteredCompanies = companies.filter(company =>
    company.company_name.toLowerCase().includes(companySearch.toLowerCase()) ||
    (company.location && company.location.toLowerCase().includes(companySearch.toLowerCase()))
  );

  const handleCompanyToggle = (companyId) => {
    const currentIds = formData.dq_company_ids;
    const isSelected = currentIds.includes(companyId);
    
    if (isSelected) {
      // Remove from selection
      setFormData({
        ...formData,
        dq_company_ids: currentIds.filter(id => id !== companyId)
      });
    } else {
      // Add to selection
      setFormData({
        ...formData,
        dq_company_ids: [...currentIds, companyId]
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (!formData.role) {
      setError('Please select your role');
      setLoading(false);
      return;
    }

    try {
      // Handle linked tour organizer + driver registration
      if (formData.role === 'guest_tour' && showLinkPrompt && existingDriver) {
        const response = await axios.post(`${BACKEND_URL}/api/auth/register-tour-organizer-linked`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          tour_role: tourRole,
        });
        // Store token and redirect
        localStorage.setItem('token', response.data.access_token);
        setSuccess(response.data.message || 'Accounts linked! Redirecting...');
        setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
        return;
      }

      let userData = {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        role: formData.role,
        phone: formData.phone || null
      };

      // Use enhanced registration endpoint for drivers with DQ companies
      if (formData.role === 'driver' && formData.dq_company_ids.length > 0) {
        userData.dq_company_ids = formData.dq_company_ids;
        
        // Call the enhanced registration endpoint
        const response = await axios.post(`${BACKEND_URL}/api/auth/register-with-dq`, userData);
        
        if (formData.dq_company_ids.length > 0) {
          setSuccess(`HopSeat account created successfully! DQ file requests sent to ${formData.dq_company_ids.length} companies. Please login to continue.`);
        } else {
          setSuccess('HopSeat account created successfully! Please login to continue.');
        }
      } else {
        // Use regular registration for non-drivers or drivers without DQ companies
        await register(userData);
        setSuccess('HopSeat account created successfully! Please login to continue.');
      }
      
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 stage-lights-bg relative overflow-hidden" style={{background: 'var(--app-bg)'}}>
      <img 
        src="https://assets.zyrosite.com/cdn-cgi/image/format=auto,w=1920,fit=crop/AR0V2zxG2NfXkkxG/jodie-walton-thm-jf3jr3w-unsplash-YbNyELbbWPHZjO99.jpg"
        alt="HopSeat Background"
        className="absolute inset-0 w-full h-full object-cover opacity-10"
      />
      <div className="max-w-md w-full relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 text-orange-500 hover:text-orange-400 mb-4">
            <img 
              src="/favicon-192x192.png?v=20260609"
              alt="HopSeat Logo"
              className="h-8 w-8 object-contain"
            />
            <span className="text-2xl font-bold hopseat-brand">HopSeat</span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">Join HopSeat</h1>
          <p className="text-gray-300">Create your account and start connecting</p>
        </div>

        {/* Registration Form */}
        <div className="form-container">
          {error && (
            <div className="alert alert-error mb-4">
              <AlertCircle className="h-5 w-5 inline mr-2" />
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success mb-4">
              <CheckCircle className="h-5 w-5 inline mr-2" />
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="full_name" className="form-label">Full Name</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Enter your full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone Number (Optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your phone number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="role" className="form-label">I am a...</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Select your role on HopSeat</option>
                {roles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {formData.role && (
                <p className="mt-2 text-sm text-gray-400">
                  <span className="text-orange-500">→</span> {roles.find(r => r.value === formData.role)?.description}
                </p>
              )}
            </div>

            {/* Existing Driver Account Detection for Tour Organizer */}
            {formData.role === 'guest_tour' && showLinkPrompt && existingDriver && (
              <div className="mb-4 p-4 rounded-xl border border-orange-500/30 bg-orange-500/10" data-testid="link-driver-prompt">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-600/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-5 w-5 text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">Driver account found!</p>
                    <p className="text-xs text-gray-400 mt-1">
                      We found an existing driver profile for <strong className="text-orange-400">{existingDriver.driver_name}</strong> with this email.
                      Your accounts will be linked so you can switch between Driver and Tour Organizer dashboards with one login.
                    </p>
                    <div className="mt-3">
                      <label className="text-xs font-medium text-gray-400 mb-1.5 block">Your tour role:</label>
                      <div className="flex flex-wrap gap-1.5">
                        {['Tour Manager', 'Production Manager', 'Lead Driver'].map(r => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setTourRole(r)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                              tourRole === r
                                ? 'bg-orange-600/20 text-orange-400 border-orange-500/40'
                                : 'bg-gray-800 text-gray-500 border-gray-700 hover:border-orange-500/30'
                            }`}
                            data-testid={`link-role-${r.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DQ File Company Selection for Drivers */}
            {formData.role === 'driver' && (
              <div className="form-group">
                <label className="form-label">
                  <Building2 className="h-4 w-4 inline mr-2" />
                  Bus Companies with DQ File (Optional)
                </label>
                <p className="text-sm text-gray-400 mb-3">
                  Select bus companies you work with or have a DQ file on record. This helps streamline gig applications and compliance requirements.
                </p>
                
                {/* Search Box */}
                <div className="relative mb-3">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search bus companies..."
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>

                {/* Company List */}
                <div className="max-h-48 overflow-y-auto border border-gray-600 rounded-lg bg-gray-800">
                  {loadingCompanies ? (
                    <div className="p-4 text-center text-gray-400">
                      <div className="loading-spinner mx-auto mb-2"></div>
                      Loading companies...
                    </div>
                  ) : filteredCompanies.length === 0 && companies.length === 0 ? (
                    <div className="p-4 text-center text-gray-400">
                      No bus companies available yet. You can add DQ file relationships later from your profile.
                    </div>
                  ) : filteredCompanies.length === 0 ? (
                    <div className="p-4 text-center text-gray-400">
                      No companies match your search. Try different keywords.
                    </div>
                  ) : (
                    filteredCompanies.map((company) => (
                      <div
                        key={company.id}
                        className={`p-3 border-b border-gray-700 last:border-b-0 cursor-pointer hover:bg-gray-700 transition-colors ${
                          formData.dq_company_ids.includes(company.id) ? 'bg-orange-900 bg-opacity-30' : ''
                        }`}
                        onClick={() => handleCompanyToggle(company.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.dq_company_ids.includes(company.id)}
                                onChange={() => handleCompanyToggle(company.id)}
                                className="mr-3 h-4 w-4 text-orange-500 border-gray-600 rounded focus:ring-orange-500 bg-gray-700"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div>
                                <h4 className="text-white font-medium">{company.company_name}</h4>
                                {company.location && (
                                  <p className="text-sm text-gray-400">{company.location}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Selection Summary */}
                {formData.dq_company_ids.length > 0 && (
                  <div className="mt-3 p-3 bg-orange-900 bg-opacity-20 border border-orange-600 rounded-lg">
                    <p className="text-sm text-orange-200">
                      <strong>{formData.dq_company_ids.length}</strong> companies selected. 
                      DQ file requests will be sent for approval after registration.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input pr-12"
                  required
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Confirm your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="loading-spinner mr-2"></div>
                  Creating Account...
                </>
              ) : (
                'Join HopSeat Community'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-300">
              Already part of the HopSeat community?{' '}
              <Link to="/login" className="text-orange-500 hover:text-orange-400 font-semibold">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;